import { TILE_FEATURES } from '@/lib/features';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Share2, BookMarked, Code2, Users, UserPlus, Search, MapPin, Building2, Phone, Mail, Calendar, FileText, ChevronRight, ArrowLeft, X, Eye, Plus, Bell, Check, XCircle, ShieldCheck, ShieldAlert, Layers, Flame, Zap, ReceiptPoundSterling, Inbox, CheckCircle2, ListChecks, Video, Image as ImageIcon, PoundSterling, Package, Truck, PackageCheck, BadgeCheck, Warehouse, FolderKanban, Hammer, Pause, GraduationCap, BookOpenText, AlertTriangle, Trash2, LifeBuoy, MessageCircle, Star, Lightbulb, MessageSquare, Megaphone, Send, TrendingUp, Globe, Briefcase, MailSearch, Lock, Clock, Link2, LayoutGrid, CalendarClock, Wallet, CreditCard, Sun, Target, HardHat, Upload, UserRound, PhoneCall } from 'lucide-react';
import SendMessageModal from '../components/messaging/SendMessageModal';
import InstallerRecentMessages from '../components/messaging/InstallerRecentMessages';
import BroadcastPicker from '../components/messaging/BroadcastPicker';
import MessageThreadModal from '../components/messaging/MessageThreadModal';
import useNotificationCounts from '@/hooks/useNotificationCounts';

import API_URL from '@/config/api';
import { toast } from 'sonner';
import { ClockCard } from '@/components/timesheets/ClockCard';
import { PushToggle } from '@/components/PushToggle';
import ProPortalHeader from '@/components/ProPortalHeader';
import { TileFavouritesProvider, FavouritesPanel, FavouriteStar, useTileFavourites } from '@/components/staff/TileFavourites';
import { SortableSection } from '@/components/staff/SortableSection';
import { SortableSections, SectionHandle } from '@/components/staff/SortableSections';
import { ApprovedBadgeList } from '@/components/certifications/ApprovedBadge';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatApiError } from '@/utils/apiError';
import { MyMobileBanner } from '@/components/staff/MyMobileBanner';
import logger from '@/utils/logger';
import useInstallerTiers from '@/hooks/useInstallerTiers';

// Role colors
const ROLE_COLORS = {
  admin: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
  manager: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  team: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  installer: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' }
};

// Service type colors
const SERVICE_COLORS = {
  probound: { bg: 'bg-[#2F5E96]/10', text: 'text-[#2F5E96]' },
  proepoxy: { bg: 'bg-[#7A0B7A]/10', text: 'text-[#7A0B7A]' },
  prodecor: { bg: 'bg-[#D5AB09]/10', text: 'text-[#D5AB09]' }
};

const STATUS_COLORS = {
  hot: { bg: 'bg-red-100', text: 'text-red-800' },
  warm: { bg: 'bg-orange-100', text: 'text-orange-800' },
  nurture: { bg: 'bg-blue-100', text: 'text-blue-800' },
  lost: { bg: 'bg-gray-100', text: 'text-gray-800' }
};

const StaffDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Deep-linking: /staff/installers, /staff/team, /staff/requests, /staff/approvals
  // all open the same dashboard but auto-select the matching panel. Keeps the
  // dashboard tiles feeling like proper pages (own URL, back button, bookmark)
  // without duplicating the (large) inline management UI into new files.
  const TAB_FROM_PATH = {
    // '/staff/installers' now routes to its own standalone ManageInstallersPage,
    // so it's intentionally NOT mapped here.
    '/staff/team': 'staff',
    '/staff/requests': 'requests',
  };
  const { user, isStaff, canCreateAccounts, canViewProfit, canManageInvoices, role, checkAuth, can } = useContext(AuthContext);
  const confirm = useConfirm();
  
  const [installers, setInstallers] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Send-message modal state — used by the SMS Text / WhatsApp buttons on installer cards.
  const [messageTarget, setMessageTarget] = useState(null); // { name, phone, ref, channel }
  const [broadcastPickerOpen, setBroadcastPickerOpen] = useState(false);
  const [broadcastConfig, setBroadcastConfig] = useState(null); // { brand_access, channel, label, matched }
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [threadTarget, setThreadTarget] = useState(null); // { id, name } for the thread modal
  const [unreadCounts, setUnreadCounts] = useState({}); // { installer_user_id: n }

  // Poll the per-installer inbound unread counts. Called on mount, every 45s,
  // and after marking a thread read so the red pill snaps to 0 instantly.
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/unread-summary`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setUnreadCounts(data.counts || {});
    } catch (_e) { /* silent — badge just won't update this tick */ }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const t = setInterval(fetchUnreadCounts, 45000);
    return () => clearInterval(t);
  }, [fetchUnreadCounts]);

  // Open a thread + optimistically clear its badge + tell backend we read it.
  const openThread = useCallback((installer) => {
    setThreadTarget({ id: installer.user_id, name: installer.name });
    setUnreadCounts((prev) => {
      if (!prev[installer.user_id]) return prev;
      const { [installer.user_id]: _dropped, ...rest } = prev;
      return rest;
    });
    (async () => {
      try {
        await fetch(`${API_URL}/api/messages/thread/mark-read`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipient_ref: installer.user_id }),
        });
      } catch (_e) { /* swallow — next poll will reconcile */ }
    })();
  }, []);

  // Optimistic consent toggle — flip locally + call API; roll back on error.
  const toggleInstallerConsent = useCallback(async (installerId, field, next) => {
    setInstallers((prev) => prev.map((i) => i.user_id === installerId ? { ...i, [field]: next } : i));
    try {
      const res = await fetch(`${API_URL}/api/staff/installers/${installerId}/consent`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: next }),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (e) {
      // Roll back on failure.
      setInstallers((prev) => prev.map((i) => i.user_id === installerId ? { ...i, [field]: !next } : i));
    }
  }, []);
  const [selectedInstaller, setSelectedInstaller] = useState(null);
  const [installerDetails, setInstallerDetails] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('installer'); // 'installer', 'team', 'manager'
  const [activeTab, setActiveTab] = useState(() => TAB_FROM_PATH[location.pathname] || 'home'); // 'home' (no content), 'installers', 'staff', 'requests'

  // Keep activeTab in sync when the user navigates via browser back/forward.
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get('create_account') === '1') {
      setNewAccount((a) => ({ ...a, role: 'installer', email: qs.get('email') || '', name: qs.get('name') || '', company_name: qs.get('company') || '', phone: qs.get('phone') || '' }));
      setShowCreateModal(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    const nextTab = TAB_FROM_PATH[location.pathname] || 'home';
    setActiveTab(nextTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const contentPanelRef = React.useRef(null);

  // Scroll the content panel into view whenever the user opens one from an action / admin tile
  useEffect(() => {
    if (activeTab !== 'home' && contentPanelRef.current) {
      contentPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeTab]);

  // Mark stale staff order notifications as read so the badge clears on next poll.
  // The Action Required panel below shows the *real* counts from the orders queue API,
  // so the in-app notification docs are redundant once staff are on the dashboard.
  useEffect(() => {
    fetch(`${API_URL}/api/notifications/mark-read-by-category/order`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  }, []);
  const [accessRequests, setAccessRequests] = useState([]);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [insuranceCounts, setInsuranceCounts] = useState({ expired: 0, expiring: 0 });
  const [pipelinePending, setPipelinePending] = useState({ count: 0, final_review: 0, partial: 0, by_stage: {} });
  const [callsPending, setCallsPending] = useState({ pending: 0, confirmed: 0 });
  const [certsByInstaller, setCertsByInstaller] = useState({}); // user_id -> [cert,...]
  const [orderQueueCounts, setOrderQueueCounts] = useState({ submitted: 0, paid: 0, invoiced: 0, dispatched: 0, urgent_submitted: 0 });
  const [supportCounts, setSupportCounts] = useState({ new: 0, awaiting_customer: 0, awaiting_ox: 0, complete: 0, new_by_priority: { urgent: 0, priority: 0, standard: 0 }, total_open: 0 });
  const [accountCounts, setAccountCounts] = useState({ new_enquiry: 0, spoken_to: 0, first_project_quoted: 0, first_order_placed: 0, active_customer: 0, total_open: 0 });
  const [favouriteBoards, setFavouriteBoards] = useState([]);
  const [websiteEnquiryCount, setWebsiteEnquiryCount] = useState(0);
  const [pendingVoucherCount, setPendingVoucherCount] = useState(0);
  const [suggestionCounts, setSuggestionCounts] = useState({ total_open: 0, new: 0 });
  const [leadBoardTotals, setLeadBoardTotals] = useState({ unassigned: 0, pending: 0, accepted: 0, rejected: 0 });
  const [projectTotals, setProjectTotals] = useState({ upcoming: 0, in_progress: 0, completed: 0, on_hold: 0, cancelled: 0, total: 0 });
  const [pricingTiers, setPricingTiers] = useState([]);
  // Installer price tiers (Trade / Approved / Elite Approved + any staff add) — the
  // columns on `stock_products.prices`. Rendered as the "Tier" dropdown per brand.
  const { tiers: installerTiers, labels: TIER_SLUG_LABELS } = useInstallerTiers();
  const TIER_SLUGS = installerTiers;
  // Unread "incoming order" notifications for staff (aggregate across all 3 sections)
  const { bySectionCategory: notifBSC } = useNotificationCounts(15000);
  const unreadIncomingOrders = (notifBSC?.probound?.order || 0) + (notifBSC?.proepoxy?.order || 0) + (notifBSC?.prodecor?.order || 0);
  
  const [newAccount, setNewAccount] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    role: 'installer',
    access: {
      probound: false,
      proepoxy: false,
      prodecor: false,
      protraining: false
    }
  });
  
  // Determine which roles current user can create
  const getCreatableRoles = () => {
    if (role === 'admin') return ['manager', 'team', 'installer'];  // Admin can create all
    if (role === 'manager') return ['team', 'installer'];
    return [];
  };
  
  // Tracks which audience the create-modal is currently aimed at — drives the
  // dropdown options and the default role selection. 'any' = user opened it
  // from the global Create Account button (installers + staff both available);
  // 'staff' = launched from inside the Admin Team tab (only manager / team).
  const [createScope, setCreateScope] = useState('any');

  // Update default role when modal opens
  const handleOpenCreateModal = (scope = 'any') => {
    const creatableRoles = getCreatableRoles();
    // For the Admin Team tab launcher we strip 'installer' so admins can't
    // accidentally create an installer when they meant a staff member.
    const scopedRoles = scope === 'staff'
      ? creatableRoles.filter(r => r !== 'installer')
      : creatableRoles;
    setCreateScope(scope);
    setNewAccount({
      email: '',
      password: '',
      name: '',
      company_name: '',
      phone: '',
      role: scopedRoles[0] || creatableRoles[0] || 'installer',
      access: {
        probound: false,
        proepoxy: false,
        prodecor: false
      }
    });
    setShowCreateModal(true);
  };

  // Decide which staff users the current viewer is allowed to delete. Mirrors
  // backend rules in routes/staff.py::delete_user_account so we don't ever show
  // a button the API will reject.
  const canDeleteStaff = (target) => {
    if (!target) return false;
    if (target.user_id === user?.user_id) return false;                // self
    if (role === 'admin')   return target.role !== 'admin';            // admins can't delete other admins
    if (role === 'manager') return target.created_by === user?.user_id; // managers only their own
    return false;
  };

  const handleDeleteStaff = async (target) => {
    if (!canDeleteStaff(target)) return;
    const ok = await confirm({
      title: `Remove ${target.name} from the admin team?`,
      body: `Role: ${target.role}\n\nThis deletes their login. They'll need a fresh invite to come back.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/users/${target.user_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to remove staff member');
      toast.success(`Removed ${target.name}`);
      fetchStaffUsers();
    } catch (e) {
      toast.error(e.message);
    }
  };
  
  // Update installer access
  const handleUpdateAccess = async (installerId, newAccess) => {
    try {
      const response = await fetch(`${API_URL}/api/staff/installers/${installerId}/access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ access: newAccess })
      });
      
      if (response.ok) {
        // Refresh data
        fetchInstallers();
        if (installerDetails?.installer?.user_id === installerId) {
          fetchInstallerDetails(installerId);
        }
      }
    } catch (error) {
      logger.error('Error updating access:', error);
    }
  };
  
  const fetchCertifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/certifications`, { credentials: 'include' });
      if (res.ok) {
        const rows = await res.json();
        const map = {};
        for (const c of rows) {
          if (!map[c.user_id]) map[c.user_id] = [];
          map[c.user_id].push(c);
        }
        setCertsByInstaller(map);
      }
    } catch (e) {
      logger.error('Failed to load certifications summary:', e);
    }
    try {
      const res2 = await fetch(`${API_URL}/api/staff/orders/summary`, { credentials: 'include' });
      if (res2.ok) {
        const s = await res2.json();
        setOrderQueueCounts(s);
      }
    } catch (e) {
      logger.error('Failed to load staff orders summary:', e);
    }
    try {
      const rWeb = await fetch(`${API_URL}/api/staff/website-enquiries/summary`, { credentials: 'include' });
      if (rWeb.ok) setWebsiteEnquiryCount((await rWeb.json()).new || 0);
    } catch (e) {
      logger.error('Failed to load website enquiries summary:', e);
    }
    try {
      const rV = await fetch(`${API_URL}/api/staff/review-vouchers`, { credentials: 'include' });
      if (rV.ok) { const c = (await rV.json()).counts || {}; setPendingVoucherCount((c.pending || 0) + (c.requests || 0)); }
    } catch (e) {
      logger.error('Failed to load review vouchers summary:', e);
    }
    try {
      const rSup = await fetch(`${API_URL}/api/staff/support/summary`, { credentials: 'include' });
      if (rSup.ok) {
        setSupportCounts(await rSup.json());
      }
    } catch (e) {
      logger.error('Failed to load support summary:', e);
    }
    try {
      const rAcc = await fetch(`${API_URL}/api/staff/accounts/summary`, { credentials: 'include' });
      if (rAcc.ok) {
        setAccountCounts(await rAcc.json());
      }
    } catch (e) {
      logger.error('Failed to load account-management summary:', e);
    }
    try {
      const rFav = await fetch(`${API_URL}/api/boards/favourites`, { credentials: 'include' });
      if (rFav.ok) setFavouriteBoards(await rFav.json());
    } catch (e) {
      logger.error('Failed to load favourite boards:', e);
    }
    try {
      const rSug = await fetch(`${API_URL}/api/staff/suggestions/summary`, { credentials: 'include' });
      if (rSug.ok) setSuggestionCounts(await rSug.json());
    } catch (e) {
      logger.error('Failed to load suggestions summary:', e);
    }
    try {
      const res3 = await fetch(`${API_URL}/api/admin/leads-board`, { credentials: 'include' });
      if (res3.ok) {
        const s = await res3.json();
        setLeadBoardTotals(s.totals || { unassigned: 0, pending: 0, accepted: 0, rejected: 0 });
      }
    } catch (e) {
      logger.error('Failed to load lead board totals:', e);
    }
    try {
      const resPr = await fetch(`${API_URL}/api/projects/summary`, { credentials: 'include' });
      if (resPr.ok) setProjectTotals(await resPr.json());
    } catch (e) {
      logger.error('Failed to load project totals:', e);
    }
    try {
      const res4 = await fetch(`${API_URL}/api/admin/pricing-tiers`, { credentials: 'include' });
      if (res4.ok) setPricingTiers(await res4.json());
    } catch (e) {
      logger.error('Failed to load pricing tiers:', e);
    }
    // Material-cost tiers no longer live in their own collection — the
    // three canonical slugs (trade / approved / elite_approved) are
    // rendered from the fixed TIER_SLUGS constant. No fetch needed.
  }, []);
  const fetchInstallers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/installers`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInstallers(data);
      }
    } catch (error) {
      logger.error('Error fetching installers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAccessRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/access-requests?status=all`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAccessRequests(data);
      }
    } catch (error) {
      logger.error('Error fetching access requests:', error);
    }
  }, []);
  const [requestFilter, setRequestFilter] = useState('pending');
  const highlightRequestId = new URLSearchParams(location.search).get('highlight');
  useEffect(() => {
    if (!highlightRequestId || accessRequests.length === 0) return;
    const target = accessRequests.find((r) => r.id === highlightRequestId);
    if (target && target.status !== 'pending') setRequestFilter('all');
    const t = setTimeout(() => document.getElementById(`access-request-${highlightRequestId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    return () => clearTimeout(t);
  }, [highlightRequestId, accessRequests]);
  const requestCounts = accessRequests.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});
  const visibleRequests = accessRequests.filter((r) => requestFilter === 'all' || r.status === requestFilter);

  const fetchPendingRequestsCount = useCallback(async () => {
    try {
      fetch(`${API_URL}/api/staff/insurance-expiry/count`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setInsuranceCounts(d); }).catch(() => {});
      const response = await fetch(`${API_URL}/api/access-requests/pending/count`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingRequestsCount(data.count);
      }
    } catch (error) {
      logger.error('Error fetching pending requests count:', error);
    }
  }, []);

  const fetchPipelinePending = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/approvals-pending-count`, { credentials: 'include' });
      if (response.ok) setPipelinePending(await response.json());
      const calls = await fetch(`${API_URL}/api/staff/strategy-calls/pending-count`, { credentials: 'include' });
      if (calls.ok) setCallsPending(await calls.json());
    } catch (error) {
      logger.error('Error fetching approval pipeline count:', error);
    }
  }, []);

  const handleApproveRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/access-requests/${requestId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchAccessRequests();
        fetchPendingRequestsCount();
        fetchInstallers();
      }
    } catch (error) {
      logger.error('Error approving request:', error);
    }
  };

  const handleDenyRequest = async (requestId) => {
    const ok = await confirm({
      title: 'Deny this access request?',
      body: 'The installer will be told their request was not approved.',
      confirmLabel: 'Deny',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/api/access-requests/${requestId}/deny`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchAccessRequests();
        fetchPendingRequestsCount();
      }
    } catch (error) {
      logger.error('Error denying request:', error);
    }
  };
  
  const fetchStaffUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStaffUsers(data);
      }
    } catch (error) {
      logger.error('Error fetching staff users:', error);
    }
  }, []);

  // 1) Auth gate — redirect non-staff users away from this page. Cheap; runs
  //    only when role-derived `isStaff` changes.
  useEffect(() => {
    if (!isStaff) navigate('/');
  }, [isStaff, navigate]);

  // 2) Always-needed staff fetches — installers, access requests, approvals, certs.
  //    Gated on `isStaff` so it doesn't fire after the redirect above kicks in.
  useEffect(() => {
    if (!isStaff) return;
    Promise.all([
      fetchInstallers(),
      fetchAccessRequests(),
      fetchPendingRequestsCount(),
      fetchCertifications(),
      fetchPipelinePending(),
    ]);
  }, [isStaff, fetchInstallers, fetchAccessRequests, fetchPendingRequestsCount, fetchCertifications, fetchPipelinePending]);

  // 3) Account-creation-only fetch — only admins/managers see the Staff Users tab,
  //    so skip the round-trip entirely for everyone else.
  useEffect(() => {
    if (!isStaff || !canCreateAccounts) return;
    fetchStaffUsers();
  }, [isStaff, canCreateAccounts, fetchStaffUsers]);

  // 4) Resend email-delivery health probe — surfaces a red banner the moment
  //    the Resend API key is invalid/expired/quota'd so we never again let
  //    outbound email silently rot for weeks.
  const [emailHealth, setEmailHealth] = useState(null);
  useEffect(() => {
    if (!isStaff) return;
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/diagnostics/resend`, { credentials: 'include' });
        if (!res.ok || !alive) return;
        setEmailHealth(await res.json());
      } catch (_) { /* transient — leave stale value */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);  // re-check every 5 min
    return () => { alive = false; clearInterval(t); };
  }, [isStaff]);
  
  const fetchInstallerDetails = async (installerId) => {
    try {
      const response = await fetch(`${API_URL}/api/staff/installers/${installerId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInstallerDetails(data);
      }
    } catch (error) {
      logger.error('Error fetching installer details:', error);
    }
  };
  
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      // Always omit password — invite-by-email flow is the only mode now.
      const { password: _omit, ...payload } = newAccount;
      const response = await fetch(`${API_URL}/api/staff/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setShowCreateModal(false);
        setNewAccount({ email: '', password: '', name: '', company_name: '', phone: '', role: 'installer', access: { probound: false, proepoxy: false, prodecor: false, protraining: false } });
        if (data.invite_sent) {
          toast.success(`Invite email sent to ${data.email}`);
        } else if (data.invite_mode) {
          toast.warning(`Account created, but the invite email failed. Use "Resend invite" on the card to retry.`);
        } else {
          toast.success(`Account created for ${data.name}`);
        }
        fetchInstallers();
        if (canCreateAccounts) fetchStaffUsers();
      } else {
        const error = await response.json();
        toast.error(formatApiError(error) || 'Failed to create account');
      }
    } catch (error) {
      logger.error('Error creating account:', error);
      toast.error('Failed to create account');
    }
  };

  const handleUpdateReportsTo = async (target, newValue) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/users/${target.user_id}/reports-to`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reports_to_user_id: newValue || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to update');
      toast.success(newValue ? `${target.name} now reports to the selected manager.` : `Cleared reports-to for ${target.name}.`);
      fetchStaffUsers();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleInvoiceAccess = async (target, enabled) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/users/${target.user_id}/invoice-access`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ enabled }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to update');
      fetchStaffUsers();
      toast.success(enabled ? `${target.name} can now manage invoices` : `Invoice access removed for ${target.name}`);
    } catch (e) { toast.error(e.message); }
  };
  const handleProfitAccess = async (target, enabled) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/users/${target.user_id}/profit-access`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to update');
      toast.success(enabled ? `${target.name} can now see profit figures.` : `${target.name} no longer sees profit figures.`);
      fetchStaffUsers();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleResendInvite = async (target) => {
    const ok = await confirm({
      title: 'Resend invite email?',
      body: `A fresh "Set password" link will be emailed to ${target.email}. The previous link (if any) will stop working.`,
      confirmLabel: 'Send',
      tone: 'primary',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/users/${target.user_id}/resend-invite`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || `Failed to resend (${res.status})`);
      if (data.ok) {
        toast.success(`Invite email re-sent to ${data.email}`);
      } else {
        toast.error(`Couldn't deliver invite to ${data.email} — check the Resend dashboard for failed sends.`);
      }
    } catch (e) {
      toast.error(e.message);
    }
  };
  
  const handleViewInstaller = (installer) => {
    setSelectedInstaller(installer);
    fetchInstallerDetails(installer.user_id);
  };
  
  const handleAddLeadToInstaller = (installer) => {
    // Navigate to leads page with installer pre-selected
    navigate(`/leads?installer=${installer.user_id}&action=add`);
  };

  const setInstallerTier = async (installerId, tierId) => {
    const prevTierId = installers.find((i) => i.user_id === installerId)?.pricing_tier_id;
    // Optimistic update
    setInstallers((prev) => prev.map((i) => (i.user_id === installerId ? { ...i, pricing_tier_id: tierId } : i)));
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${installerId}/pricing-tier`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing_tier_id: tierId }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed to set tier');
      const label = pricingTiers.find((t) => t.tier_id === tierId)?.label || 'tier';
      toast.success(`Moved to ${label}`);
    } catch (e) {
      // Rollback on failure
      setInstallers((prev) => prev.map((i) => (i.user_id === installerId ? { ...i, pricing_tier_id: prevTierId } : i)));
      toast.error(e.message);
    }
  };
  
  const setInstallerMaterialCostTier = async (installerId, brand, slug) => {
    const installer = installers.find((i) => i.user_id === installerId);
    const prev = installer?.pricing_tier_by_brand || {};
    const next = { ...prev, [brand]: slug || null };
    setInstallers((prevList) => prevList.map((i) => (i.user_id === installerId ? { ...i, pricing_tier_by_brand: next } : i)));
    try {
      const res = await fetch(`${API_URL}/api/staff/users/${installerId}/pricing-tier-by-brand`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed to set tier');
      const label = TIER_SLUG_LABELS[slug];
      toast.success(label ? `${brand} → ${label}` : `${brand} tier cleared`);
    } catch (e) {
      setInstallers((prevList) => prevList.map((i) => (i.user_id === installerId ? { ...i, pricing_tier_by_brand: prev } : i)));
      toast.error(e.message);
    }
  };

  const filteredInstallers = installers.filter(installer =>
    installer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    installer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    installer.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (!isStaff) {
    return null;
  }
  
  return (
    <TileFavouritesProvider>
    <div className="min-h-screen bg-gray-50">
      <ProPortalHeader activeSection="staff" />

      {/* Sub-header: title + Create Account CTA */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center gap-2 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-bold text-[#27292E] truncate">Staff Dashboard</h2>
              <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-bold ${ROLE_COLORS[role]?.bg} ${ROLE_COLORS[role]?.text}`}>
                {role?.charAt(0).toUpperCase() + role?.slice(1)}
              </span>
            </div>
            <p className="hidden sm:block text-gray-500 text-xs mt-0.5">Operations · People · Admin</p>
          </div>
          <Button
            onClick={() => navigate('/staff/help')}
            size="sm"
            variant="outline"
            className="border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/10 px-2 sm:px-3 shrink-0"
            aria-label="Help & user manual"
            data-testid="staff-help-btn"
          >
            <BookOpenText className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Help</span>
          </Button>
          <CustomiseDashboardButton />
          {canCreateAccounts && (
            <Button
              onClick={() => handleOpenCreateModal('any')}
              size="sm"
              className="bg-[#27292E] text-white hover:bg-[#3a3d45] px-2 sm:px-3 shrink-0"
              aria-label="Create Account"
              data-testid="staff-create-account-btn"
            >
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Create Account</span>
            </Button>
          )}
        </div>
      </div>
      
      {/* Ask every staff member for a mobile so texts (@mentions, nudges) can reach them */}
      <MyMobileBanner user={user} onSaved={() => { checkAuth(); fetchStaffUsers(); }} />

      {/* Clock in / change task / clock out — one tap on arrival */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-3 space-y-2">{can('clock_in') && <ClockCard compact />}{(user?.role === 'admin' || user?.role === 'manager') && <div className="flex justify-end items-center gap-3"><PushToggle compact /><button onClick={() => navigate('/staff/push-notifications')} className="text-xs text-gray-600 underline inline-flex items-center gap-1" data-testid="push-settings-link"><Bell className="w-3.5 h-3.5" /> Push notification settings</button></div>}</div>

      {/* Email delivery health banner — lights up red the moment Resend
          is misconfigured (placeholder key, expired key, network reject).
          Prevents the 2-month silent-failure that previously bit us. */}
      {emailHealth && emailHealth.ok === false && (
        <div
          data-testid="email-health-banner"
          className="max-w-7xl mx-auto px-3 sm:px-4 mt-2"
        >
          <div className="w-full flex items-center gap-3 bg-red-50 border border-red-300 text-red-900 rounded-lg px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">
                Email delivery is DOWN — outbound emails are not being sent
              </div>
              <div className="text-xs mt-0.5 text-red-800">
                {emailHealth.error || 'Resend rejected the last send attempt'}. Every welcome, quote, invoice, password-reset and lead notification is failing silently. Update <code className="bg-red-100 px-1 rounded">RESEND_API_KEY</code> in <code className="bg-red-100 px-1 rounded">backend/.env</code> and restart the backend to restore delivery.
              </div>
            </div>
            <a
              href="/staff/diagnostics/email"
              className="text-xs font-bold underline shrink-0 whitespace-nowrap"
              data-testid="email-health-details-link"
            >
              View log →
            </a>
          </div>
        </div>
      )}

      {/* Raise-Invoice reminder banner — visible whenever there are urgent submitted orders.
          Backend also fires an email digest (Mon-Fri, 09:00-17:00 UK) to orders@oxresin.co.uk. */}
      {(orderQueueCounts.urgent_submitted || 0) > 0 && (
        <div
          data-testid="raise-invoice-banner"
          className="max-w-7xl mx-auto px-3 sm:px-4 mt-2"
        >
          <button
            type="button"
            onClick={() => navigate('/staff/orders?tab=submitted&priority=urgent')}
            className="w-full flex items-center gap-3 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-900 text-left rounded-lg px-4 py-3 transition"
          >
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">
                {orderQueueCounts.urgent_submitted} urgent invoice{orderQueueCounts.urgent_submitted === 1 ? '' : 's'} waiting to be raised in Xero
              </div>
              <div className="text-xs text-rose-700/80">
                Email reminders go to orders@oxresin.co.uk every 30 min during working hours (Mon-Fri, 9-5). Click to open the queue.
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-rose-600 shrink-0" />
          </button>
        </div>
      )}

      <FavouritesPanel renderTile={(p) => <DashboardTile {...p} />} />

      <SortableSections SectionComponent={SectionPanel} isAdmin={role === 'admin'}>
      {/* ===== MY DAY ===== */}
      <SectionPanel
        testId="my-day-panel"
        title="My Day"
        description="Your own timesheet, boards and ideas — the personal bits of the portal"
        icon={<Sun className="w-4 h-4" />}
        accent="bg-amber-500"
        tint="bg-amber-50 border-amber-200"
      >
        {(user?.clock_required !== false || user?.role === 'admin' || user?.role === 'manager') && <DashboardTile
          testId="timesheets-tile"
          onClick={() => navigate('/staff/timesheets')}
          icon={<Clock className="w-4 h-4 text-slate-700" />}
          iconWrap="bg-slate-200"
          hoverBorder="hover:border-slate-500"
          title="Timesheets"
          subtitle="Log what you worked on today — slide to set the hours, see your week or month."
        />}
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <DashboardTile
            testId="attendance-tile"
            onClick={() => navigate('/staff/attendance')}
            icon={<UserRound className="w-4 h-4 text-sky-700" />}
            iconWrap="bg-sky-100"
            hoverBorder="hover:border-sky-500"
            title="Staff Attendance"
            subtitle="Pick a person, see their week — clock in/out times, where they were, breaks. Tap a day for detail."
          />
        )}
        <DashboardTile
          testId="custom-boards-tile"
          onClick={() => navigate('/boards')}
          icon={<Layers className="w-4 h-4 text-fuchsia-700" />}
          iconWrap="bg-fuchsia-100"
          hoverBorder="hover:border-fuchsia-400"
          title="My Boards"
          subtitle="Build your own Kanban with stages that make sense for you — sales pipelines, project trackers, to-do lists"
        />
        <DashboardTile
          testId="suggestions-submit-tile"
          onClick={() => navigate('/suggest')}
          icon={<Lightbulb className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Suggest an Improvement"
          subtitle="Got an idea to make the portal better? Send it through — we read every one."
        />
        {favouriteBoards.map((b) => (
          <DashboardTile
            key={b.id}
            testId={`fav-board-tile-${b.id}`}
            onClick={() => navigate(`/boards/${b.id}`)}
            icon={<Star className="w-4 h-4 text-amber-500 fill-amber-400" />}
            iconWrap="bg-amber-50"
            hoverBorder="hover:border-amber-400"
            title={b.name}
            subtitle={
              (b.card_count || 0) > 0
                ? `${b.card_count} card${b.card_count === 1 ? '' : 's'} · ${(b.columns || []).length} columns`
                : `${(b.columns || []).length} columns · empty so far`
            }
          />
        ))}
        {favouriteBoards.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 text-[11px] text-gray-500 px-1 italic">
            Tip: tap the ⭐ on any board to pin it here for one-click access.
          </div>
        )}
      </SectionPanel>
      {/* ===== SALES & MONEY ===== */}
      <SectionPanel
        testId="sales-money-panel"
        title="Sales and Financial"
        description="Cash in, costs, profit, invoices and accounting across every brand"
        icon={<PoundSterling className="w-4 h-4" />}
        accent="bg-emerald-600"
        tint="bg-emerald-50 border-emerald-200"
      >
        <DashboardTile
          testId="sales-profit-tile"
          onClick={() => navigate('/staff/sales-profit')}
          icon={<TrendingUp className="w-4 h-4 text-[#1f4030]" />}
          iconWrap="bg-[#e6efe9]"
          hoverBorder="hover:border-[#1f4030]"
          title="Sales Log"
          subtitle="Log orders and cash received · sales by day, week or month"
        />
        {canViewProfit && (
          <DashboardTile
            testId="profit-centre-tile"
            onClick={() => navigate('/staff/profit-centre')}
            icon={<Lock className="w-4 h-4 text-amber-800" />}
            iconWrap="bg-amber-100"
            hoverBorder="hover:border-amber-500"
            title="Profit Centre"
            subtitle="Private · costs, profit & margin for training + sales · cash log"
          />
        )}
        {canManageInvoices && (
            <DashboardTile
              testId="new-invoice-tile"
              onClick={() => navigate('/staff/invoices/new')}
              icon={<Plus className="w-4 h-4 text-white" />}
              iconWrap="bg-emerald-600"
              hoverBorder="hover:border-emerald-500"
              title="New Invoice"
              subtitle="Build an invoice or quote like in Xero — pick the business, customer and lines, then approve or save as draft"
            />
          )}
        {canManageInvoices && (
            <DashboardTile
              testId="invoice-centre"
              onClick={() => navigate('/staff/invoices')}
              icon={<ReceiptPoundSterling className="w-4 h-4 text-emerald-700" />}
              iconWrap="bg-emerald-100"
              hoverBorder="hover:border-emerald-300"
              title="Invoice Centre"
              subtitle="Every quote & invoice across ProBound / ProEpoxy / ProTraining · raise, approve, email, incoming payments"
            />
          )}
        <DashboardTile
          testId="invoice-queue-board"
          onClick={() => navigate('/staff/orders')}
          icon={<ReceiptPoundSterling className="w-4 h-4 text-red-600" />}
          iconWrap="bg-red-100"
          hoverBorder="hover:border-red-400"
          title="Invoice Board"
          subtitle={(orderQueueCounts.submitted || 0) + (orderQueueCounts.invoiced || 0) > 0
            ? `${(orderQueueCounts.submitted || 0) + (orderQueueCounts.invoiced || 0)} open · ${orderQueueCounts.submitted || 0} to invoice · ${orderQueueCounts.invoiced || 0} awaiting payment`
            : 'All caught up — nothing to invoice'}
          badge={(orderQueueCounts.submitted || 0) + (orderQueueCounts.invoiced || 0)}
        />
        <DashboardTile
          testId="business-overview-tile"
          onClick={() => navigate('/staff/business-overview')}
          icon={<Briefcase className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Business Overview"
          subtitle="Sales by brand & business area · date + product filters"
        />
        <div className="sm:col-span-2 lg:col-span-3 text-[11px] text-gray-500 px-1 italic">
          Tip: an invoice that sits for 24 h auto-promotes — Standard → Priority → Urgent. Once paid it graduates to the Orders Board for fulfilment.
        </div>
      </SectionPanel>
      {/* ===== ORDERS & WAREHOUSE ===== */}
      <SectionPanel
        testId="orders-warehouse-panel"
        title="Orders & Warehouse"
        description="From paid order to packed, stock-adjusted and shipped — plus stock levels, deliveries and checks"
        icon={<Warehouse className="w-4 h-4" />}
        accent="bg-blue-600"
        tint="bg-blue-50 border-blue-200"
      >
        <DashboardTile
          testId="orders-board-tile"
          onClick={() => navigate('/staff/order-processing')}
          icon={<Truck className="w-4 h-4 text-emerald-700" />}
          iconWrap="bg-emerald-100"
          hoverBorder="hover:border-emerald-400"
          title="Orders Board"
          subtitle={(() => {
            const paid = (orderQueueCounts.paid || 0) + (orderQueueCounts.payment_confirmed || 0);
            const processed = orderQueueCounts.processed || 0;
            const transit = (orderQueueCounts.in_transit || 0) + (orderQueueCounts.dispatched || 0);
            const openFulfilment = paid + processed + transit;
            return openFulfilment > 0
              ? `${openFulfilment} in fulfilment · ${paid} paid · ${processed} processed · ${transit} in transit`
              : 'Nothing in fulfilment right now';
          })()}
          badge={(orderQueueCounts.paid || 0) + (orderQueueCounts.payment_confirmed || 0) + (orderQueueCounts.processed || 0) + (orderQueueCounts.in_transit || 0) + (orderQueueCounts.dispatched || 0)}
        />
        <DashboardTile
          testId="packing-area-tile"
          onClick={() => navigate('/staff/packing')}
          icon={<Package className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Packing & Stock Out"
          subtitle={(() => {
            const toPack = (orderQueueCounts.paid || 0) + (orderQueueCounts.payment_confirmed || 0);
            const packed = orderQueueCounts.processed || 0;
            return toPack > 0 ? `${toPack} to pack · ${packed} packed awaiting courier · add manual orders` : packed > 0 ? `${packed} packed awaiting courier · add manual orders` : 'Nothing to pack · add a manual order';
          })()}
          badge={(orderQueueCounts.paid || 0) + (orderQueueCounts.payment_confirmed || 0)}
          badgeTone="amber"
        />
        <DashboardTile
          testId="stock-current-tile"
          onClick={() => navigate('/staff/stock')}
          icon={<Warehouse className="w-4 h-4 text-emerald-700" />}
          iconWrap="bg-emerald-100"
          hoverBorder="hover:border-emerald-400"
          title="Current Stock"
          subtitle="Read-only catalogue · qty per product"
        />
        <DashboardTile
          testId="stock-delivery-tile"
          onClick={() => navigate('/staff/stock/delivery')}
          icon={<Truck className="w-4 h-4 text-blue-700" />}
          iconWrap="bg-blue-100"
          hoverBorder="hover:border-blue-400"
          title="Add a Delivery"
          subtitle="Book stock in · emails orders@"
        />
        <DashboardTile
          testId="stock-check-tile"
          onClick={() => navigate('/staff/stock/check')}
          icon={<ListChecks className="w-4 h-4 text-rose-700" />}
          iconWrap="bg-rose-100"
          hoverBorder="hover:border-rose-400"
          title="Stock Check"
          subtitle="Confirm counts · flag discrepancies"
        />
        <DashboardTile
          testId="order-tile-delivered-archive"
          onClick={() => navigate('/staff/orders/delivered-archive')}
          icon={<PackageCheck className="w-4 h-4 text-slate-700" />}
          iconWrap="bg-slate-100"
          hoverBorder="hover:border-slate-400"
          title="Delivered Archive"
          subtitle={(orderQueueCounts.delivered_archived || 0) > 0
            ? `${orderQueueCounts.delivered_archived} auto-archived after 14 days on Delivered`
            : 'Deliveries drop here automatically after 14 days'}
          badge={orderQueueCounts.delivered_archived || 0}
        />
        <DashboardTile
          testId="order-tile-recently-deleted"
          onClick={() => navigate('/staff/orders/recently-deleted')}
          icon={<Trash2 className="w-4 h-4 text-rose-700" />}
          iconWrap="bg-rose-100"
          hoverBorder="hover:border-rose-400"
          title="Recently Deleted"
          subtitle={(orderQueueCounts.recently_deleted || 0) > 0
            ? `${orderQueueCounts.recently_deleted} order${orderQueueCounts.recently_deleted === 1 ? '' : 's'} in the 14-day recycle bin`
            : 'Nothing archived — bin is empty'}
          badge={orderQueueCounts.recently_deleted || 0}
        />
        
      </SectionPanel>
      {/* ===== LEADS & PROJECTS ===== */}
      <SectionPanel
        testId="leads-projects-panel"
        title="Leads & Projects"
        description="Incoming leads, who they went to, and the projects they turn into — plus lead credit and plans"
        icon={<Target className="w-4 h-4" />}
        accent="bg-violet-600"
        tint="bg-violet-50 border-violet-200"
      >
        <DashboardTile
          testId="lead-tile-unassigned"
          onClick={() => navigate('/staff/lead-management?status=unassigned')}
          icon={<Inbox className="w-4 h-4 text-blue-700" />}
          iconWrap="bg-blue-100"
          hoverBorder="hover:border-blue-400"
          title="New / Unassigned"
          subtitle={leadBoardTotals.unassigned > 0
            ? `${leadBoardTotals.unassigned} ready to triage`
            : 'No new enquiries'}
          badge={leadBoardTotals.unassigned || 0}
        />
        <DashboardTile
          testId="lead-tile-pending"
          onClick={() => navigate('/staff/lead-management?status=pending')}
          icon={<Layers className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Assigned / Pending Confirmation"
          subtitle={leadBoardTotals.pending > 0
            ? `${leadBoardTotals.pending} awaiting installer response`
            : 'Nothing in flight'}
          badge={leadBoardTotals.pending || 0}
        />
        <DashboardTile
          testId="lead-tile-accepted"
          onClick={() => navigate('/staff/lead-management?status=accepted')}
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-700" />}
          iconWrap="bg-emerald-100"
          hoverBorder="hover:border-emerald-400"
          title="Accepted"
          subtitle={leadBoardTotals.accepted > 0
            ? `${leadBoardTotals.accepted} with installers`
            : 'No accepted leads'}
          badge={leadBoardTotals.accepted || 0}
          badgeTone="emerald"
        />
        <DashboardTile
          testId="lead-tile-rejected"
          onClick={() => navigate('/staff/lead-management?status=rejected')}
          icon={<XCircle className="w-4 h-4 text-rose-700" />}
          iconWrap="bg-rose-100"
          hoverBorder="hover:border-rose-400"
          title="Rejected"
          subtitle={leadBoardTotals.rejected > 0
            ? `${leadBoardTotals.rejected} need re-assignment`
            : 'No rejected leads'}
          badge={leadBoardTotals.rejected || 0}
        />
        <DashboardTile
          testId="project-tile-upcoming"
          onClick={() => navigate('/staff/projects?status=upcoming')}
          icon={<Calendar className="w-4 h-4 text-blue-700" />}
          iconWrap="bg-blue-100"
          hoverBorder="hover:border-blue-400"
          title="Upcoming"
          subtitle={projectTotals.upcoming > 0
            ? `${projectTotals.upcoming} scheduled to start`
            : 'No projects in the diary'}
          badge={projectTotals.upcoming || 0}
        />
        <DashboardTile
          testId="project-tile-in-progress"
          onClick={() => navigate('/staff/projects?status=in_progress')}
          icon={<Hammer className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="In Progress"
          subtitle={projectTotals.in_progress > 0
            ? `${projectTotals.in_progress} on the tools`
            : 'Nothing on site right now'}
          badge={projectTotals.in_progress || 0}
        />
        <DashboardTile
          testId="project-tile-completed"
          onClick={() => navigate('/staff/projects?status=completed')}
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-700" />}
          iconWrap="bg-emerald-100"
          hoverBorder="hover:border-emerald-400"
          title="Completed"
          subtitle={projectTotals.completed > 0
            ? `${projectTotals.completed} signed off`
            : 'No completions yet'}
          badge={projectTotals.completed || 0}
          badgeTone="emerald"
        />
        <DashboardTile
          testId="project-tile-on-hold"
          onClick={() => navigate('/staff/projects?status=on_hold')}
          icon={<Pause className="w-4 h-4 text-gray-700" />}
          iconWrap="bg-gray-100"
          hoverBorder="hover:border-gray-400"
          title="On Hold"
          subtitle={projectTotals.on_hold > 0
            ? `${projectTotals.on_hold} paused — needs a nudge`
            : 'Nothing parked'}
          badge={projectTotals.on_hold || 0}
        />
        <DashboardTile
          testId="project-tile-cancelled"
          onClick={() => navigate('/staff/projects?status=cancelled')}
          icon={<XCircle className="w-4 h-4 text-rose-700" />}
          iconWrap="bg-rose-100"
          hoverBorder="hover:border-rose-400"
          title="Cancelled"
          subtitle={projectTotals.cancelled > 0
            ? `${projectTotals.cancelled} closed out`
            : 'No cancellations'}
          badge={projectTotals.cancelled || 0}
        />
      </SectionPanel>
      {/* ===== INSTALLERS ===== */}
      <SectionPanel
        testId="installers-panel"
        title="Installers"
        description="Everyone who is — or is becoming — an installer: approvals, map, coverage, insurance, prospects and ambassadors"
        icon={<HardHat className="w-4 h-4" />}
        accent="bg-orange-500"
        tint="bg-orange-50 border-orange-200"
      >
        <DashboardTile
          testId="import-contacts-tile"
          onClick={() => navigate('/staff/import-contacts')}
          icon={<Upload className="w-4 h-4 text-orange-700" />}
          iconWrap="bg-orange-100"
          hoverBorder="hover:border-orange-400"
          title="Import Contacts & Win-back"
          subtitle="Upload old customer lists · tag · email a personal invite"
        />
        <DashboardTile
            testId="admin-manage-installers"
            onClick={() => navigate('/staff/installers')}
            icon={<Users className="w-4 h-4 text-[#7A0B7A]" />}
            iconWrap="bg-[#7A0B7A]/10"
            hoverBorder="hover:border-[#7A0B7A]"
            title="Manage Installers"
            subtitle={`View, edit and add (${installers.length})`}
          />
        <DashboardTile
            testId="installer-approvals-tile"
            onClick={() => navigate('/staff/approvals')}
            icon={<ShieldCheck className="w-4 h-4 text-purple-700" />}
            iconWrap="bg-purple-100"
            hoverBorder="hover:border-purple-400"
            title="Installer Approvals"
            subtitle={pipelinePending.count > 0
              ? `${pipelinePending.count} ready for your review${pipelinePending.partial ? ` · ${pipelinePending.partial} still completing` : ''}`
              : pipelinePending.partial
                ? `${pipelinePending.partial} installer${pipelinePending.partial === 1 ? '' : 's'} still completing stages`
                : '8-stage Ox Approved review pipeline'}
            badge={pipelinePending.count || 0}
          />
        <DashboardTile
            testId="installer-portfolios-tile"
            onClick={() => navigate('/staff/portfolios')}
            icon={<ImageIcon className="w-4 h-4 text-fuchsia-700" />}
            iconWrap="bg-fuchsia-100"
            hoverBorder="hover:border-fuchsia-400"
            title="Installer Portfolios"
            subtitle="Every project installers add — before / during / finished photos, by brand and installer"
          />
        <DashboardTile
            testId="installer-checklist-tile"
            onClick={() => navigate('/staff/installer-checklist')}
            icon={<Globe className="w-4 h-4 text-violet-700" />}
            iconWrap="bg-violet-100"
            hoverBorder="hover:border-violet-400"
            title="Installer Checklist & Upsells"
            subtitle="Trained installers who still need a website, socials or insurance to get Ox Approved — sell them what's missing"
          />
        <DashboardTile
            testId="installer-map-tile"
            onClick={() => navigate('/staff/installer-map')}
            icon={<MapPin className="w-4 h-4 text-[#006400]" />}
            iconWrap="bg-[#006400]/10"
            hoverBorder="hover:border-[#006400]"
            title="Installer Map"
            subtitle="Where installers are · filter by sector & Ox Approved · nearest to a postcode"
          />
        <DashboardTile
            testId="coverage-gaps-tile"
            onClick={() => navigate('/staff/coverage-gaps')}
            icon={<Flame className="w-4 h-4 text-red-600" />}
            iconWrap="bg-red-600/10"
            hoverBorder="hover:border-red-600"
            title="Coverage Gaps"
            subtitle="Postcodes with leads but no installer in range — recruit here"
          />
        <DashboardTile
            testId="insurance-expiry-tile"
            onClick={() => navigate('/staff/insurance-expiry')}
            icon={<ShieldAlert className="w-4 h-4 text-amber-600" />}
            iconWrap="bg-amber-500/10"
            hoverBorder="hover:border-amber-500"
            title="Insurance Expiry"
            subtitle={insuranceCounts.expired + insuranceCounts.expiring > 0 ? `${insuranceCounts.expired} expired · ${insuranceCounts.expiring} expiring within 30 days` : 'All approved installers in date · auto-reminders at 30 days'}
            badge={insuranceCounts.expired + insuranceCounts.expiring}
            badgeTone={insuranceCounts.expired > 0 ? 'rose' : 'amber'}
          />
        <DashboardTile
            testId="marketing-calls-tile"
            onClick={() => navigate('/staff/marketing-calls')}
            icon={<Phone className="w-4 h-4 text-sky-700" />}
            iconWrap="bg-sky-100"
            hoverBorder="hover:border-sky-400"
            title="Marketing Calls"
            subtitle={callsPending.pending > 0
              ? `${callsPending.pending} request${callsPending.pending === 1 ? '' : 's'} awaiting confirmation${callsPending.confirmed ? ` · ${callsPending.confirmed} confirmed` : ''}`
              : callsPending.confirmed ? `${callsPending.confirmed} confirmed call${callsPending.confirmed === 1 ? '' : 's'} coming up` : '"Book My Marketing Call" requests from installers'}
            badge={callsPending.pending || 0}
          />
        <DashboardTile
            testId="admin-access-requests"
            onClick={() => navigate('/staff/requests')}
            icon={<Bell className="w-4 h-4 text-blue-700" />}
            iconWrap="bg-blue-100"
            hoverBorder="hover:border-blue-400"
            title="Access Requests"
            subtitle={pendingRequestsCount > 0
              ? `${pendingRequestsCount} waiting for portal access`
              : 'All clear — no pending requests'}
            badge={pendingRequestsCount || 0}
          />
        <DashboardTile
          testId="account-management-board"
          onClick={() => navigate('/staff/accounts')}
          icon={<UserPlus className="w-4 h-4 text-indigo-700" />}
          iconWrap="bg-indigo-100"
          hoverBorder="hover:border-indigo-400"
          title="Prospect Pipeline"
          subtitle={(accountCounts.total_open || 0) > 0
            ? `${accountCounts.total_open} in play · ${accountCounts.new_enquiry || 0} new · ${accountCounts.spoken_to || 0} spoken to · ${accountCounts.first_project_quoted || 0} quoted`
            : 'No prospects yet — add one to get started'}
          badge={accountCounts.total_open || 0}
        />
        <DashboardTile
            testId="affiliates-tile"
            onClick={() => navigate('/staff/affiliates')}
            icon={<Users className="w-4 h-4 text-fuchsia-700" />}
            iconWrap="bg-fuchsia-100"
            hoverBorder="hover:border-fuchsia-400"
            title="Manage Ambassadors"
            subtitle="Ambassador commission tracking & payouts"
          />
        <div className="sm:col-span-2 lg:col-span-3 text-[11px] text-gray-500 px-1 italic">
          Tip: bulk-import Facebook Lead Ads exports via the Import CSV tab in "New Enquiry" — duplicates are auto-skipped.
        </div>
      </SectionPanel>
      {/* ===== TRAINING ===== */}
      <SectionPanel
        testId="training-panel"
        title="Training"
        description="Courses, enquiries and analytics — plus the videos, notes and pricing trainees learn from"
        icon={<GraduationCap className="w-4 h-4" />}
        accent="bg-rose-600"
        tint="bg-rose-50 border-rose-200"
      >
        <DashboardTile
          testId="training-courses-tile"
          onClick={() => navigate('/staff/training-courses')}
          icon={<BookOpenText className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Training Courses"
          subtitle="Register + QR sign-in · auto-grants section access"
        />
        <DashboardTile
          testId="training-enquiries-tile"
          onClick={() => navigate('/staff/training-enquiries')}
          icon={<GraduationCap className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Training Enquiries"
          subtitle="Website embed inbox · emails training@"
        />
        <DashboardTile
          testId="website-enquiries-tile"
          onClick={() => navigate('/staff/website-enquiries')}
          icon={<Inbox className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Website Enquiries & Contacts"
          subtitle="Every form on oxresinprotraining.co.uk · one card per person · WhatsApp-ready"
          badge={websiteEnquiryCount}
        />
        <DashboardTile
          testId="contact-form-code-tile"
          onClick={() => navigate('/staff/contact-form-code')}
          icon={<Code2 className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Contact Form Code"
          subtitle="Iframe embeds for the 4 website enquiry forms"
        />
        <DashboardTile
          testId="advisor-diary-tile"
          onClick={() => navigate('/staff/advisor-diary')}
          icon={<PhoneCall className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Course Advisor Diary"
          subtitle="Chat bookings from Meta ads & the booking page · Google Calendar · replaces Calendly"
        />
        <DashboardTile
          testId="training-analytics-tile"
          onClick={() => navigate('/staff/training-analytics')}
          icon={<TrendingUp className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Training Analytics"
          subtitle="Monthly turnover · cash in · no-shows · lead sources"
        />
        <DashboardTile
          testId="review-vouchers-tile"
          onClick={() => navigate('/staff/review-vouchers')}
          icon={<Star className="w-4 h-4 text-amber-700" />}
          iconWrap="bg-amber-100"
          hoverBorder="hover:border-amber-400"
          title="Google Review Vouchers"
          subtitle="Approve trainee review screenshots · £ off their next order"
          badge={pendingVoucherCount}
        />
        <DashboardTile
            testId="content-videos"
            onClick={() => navigate('/staff/videos')}
            icon={<Video className="w-4 h-4 text-red-600" />}
            iconWrap="bg-red-100"
            hoverBorder="hover:border-red-300"
            title="Videos"
            subtitle="Tutorials and section walkthroughs"
          />
        <DashboardTile
            testId="content-course-notes"
            onClick={() => navigate('/staff/course-notes')}
            icon={<BookOpenText className="w-4 h-4 text-orange-700" />}
            iconWrap="bg-orange-100"
            hoverBorder="hover:border-orange-300"
            title="Course Notes"
            subtitle="One PDF per brand · ProBound / ProEpoxy / ProDecor"
          />
      </SectionPanel>
      {/* ===== WEBSITES & MARKETING ===== */}
      <SectionPanel
        testId="websites-marketing-panel"
        title="Websites & Marketing"
        description="Everything that helps installers look professional and win work — websites, domains, brochures, templates and post ideas"
        icon={<Globe className="w-4 h-4" />}
        accent="bg-teal-600"
        tint="bg-teal-50 border-teal-200"
      >
        <DashboardTile
          testId="website-orders-tile"
          onClick={() => navigate('/staff/website-orders')}
          icon={<Globe className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Website Orders"
          subtitle="Every trainee website · assign, status, domain & email records"
        />
        <DashboardTile
          testId="staff-social-tile"
          onClick={() => navigate('/staff/social')}
          icon={<Share2 className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Social Posting"
          subtitle="Post to Ox Resin's Facebook & Instagram · ready-made posts for installers"
        />
        <DashboardTile
          testId="marketing-onboarding-tile"
          onClick={() => navigate('/staff/marketing-onboarding')}
          icon={<Megaphone className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Marketing Onboarding"
          subtitle="CRM + Marketing members · ad budget, areas & goals · set-up status"
        />
        <DashboardTile
          testId="website-templates-tile"
          onClick={() => navigate('/staff/website-templates')}
          icon={<LayoutGrid className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Website Advert & Designs"
          subtitle="The advert installers see on My Website · internal designs for the generator"
        />
        <DashboardTile
          testId="website-services-tile"
          onClick={() => navigate('/staff/website-services')}
          icon={<ListChecks className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Website Services"
          subtitle="Categories & services trainees tick in the wizard · admin edits"
        />
        <DashboardTile
          testId="domain-renewals-tile"
          onClick={() => navigate('/staff/domain-renewals')}
          icon={<CalendarClock className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Domain Renewals"
          subtitle="Every domain we look after · 30-day reminders · legacy websites"
        />
        <DashboardTile
          testId="website-log-tile"
          onClick={() => navigate('/staff/website-log')}
          icon={<BookMarked className="w-4 h-4 text-teal-700" />}
          iconWrap="bg-teal-100"
          hoverBorder="hover:border-teal-400"
          title="Website Log"
          subtitle="Every website we've ever built · paste in from the spreadsheet"
        />
        <DashboardTile
            testId="content-marketing"
            onClick={() => navigate('/staff/marketing-material')}
            icon={<ImageIcon className="w-4 h-4 text-amber-700" />}
            iconWrap="bg-amber-100"
            hoverBorder="hover:border-amber-300"
            title="Marketing Material"
            subtitle="Brochures and assets per section"
          />
        <DashboardTile
          testId="marketing-tile"
          onClick={() => navigate('/staff/marketing')}
          icon={<Megaphone className="w-4 h-4 text-fuchsia-700" />}
          iconWrap="bg-fuchsia-100"
          hoverBorder="hover:border-fuchsia-400"
          title="Marketing Ideas"
          subtitle="Seasonal post ideas & text templates installers see · usage report"
        />
        <DashboardTile
            testId="content-templates"
            onClick={() => navigate('/staff/templates')}
            icon={<FileText className="w-4 h-4 text-blue-700" />}
            iconWrap="bg-blue-100"
            hoverBorder="hover:border-blue-300"
            title="Templates"
            subtitle="Quote letters, RAMS, customer emails"
          />
      </SectionPanel>
      {/* ===== COMMS & SUPPORT ===== */}
      <SectionPanel
        testId="comms-support-panel"
        title="Comms & Support"
        description="All communication with installers and trainees — automated messages, sent log, email templates, support tickets and suggestions"
        icon={<MessageSquare className="w-4 h-4" />}
        accent="bg-sky-600"
        tint="bg-sky-50 border-sky-200"
      >
        <DashboardTile
            testId="message-centre"
            onClick={() => navigate('/staff/message-centre')}
            icon={<Send className="w-4 h-4 text-[#1f4030]" />}
            iconWrap="bg-[#e6efe9]"
            hoverBorder="hover:border-[#1f4030]"
            title="Email & Message Centre"
            subtitle="Every email & text in one place — edit wording, venue, start times · who gets what and when"
          />
        <DashboardTile
          testId="support-tile-board"
          onClick={() => navigate('/staff/support')}
          icon={<LifeBuoy className="w-4 h-4 text-slate-700" />}
          iconWrap="bg-slate-100"
          hoverBorder="hover:border-slate-400"
          title="Support Board"
          subtitle={(supportCounts.total_open || 0) > 0
            ? `${supportCounts.total_open} open · ${supportCounts.new_by_priority?.urgent || 0} urgent · ${supportCounts.new_by_priority?.priority || 0} priority`
            : 'All caught up — no open requests'}
          badge={supportCounts.total_open || 0}
        />
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <DashboardTile
            testId="suggestions-board-tile"
            onClick={() => navigate('/staff/suggestions')}
            icon={<Layers className="w-4 h-4 text-amber-700" />}
            iconWrap="bg-amber-100"
            hoverBorder="hover:border-amber-400"
            title="Suggestions Board"
            subtitle="Review every submitted idea and progress it through New → Under Review → Planned → Completed."
            badge={suggestionCounts.total_open || 0}
          />
        )}
        <div className="sm:col-span-2 lg:col-span-3 text-[11px] text-gray-500 px-1 italic">
          Tip: like invoices, a support request that sits for 24 h auto-promotes — Standard → Priority → Urgent — so nothing falls through the cracks.
        </div>
      </SectionPanel>
      {/* ===== ADMIN ===== */}
      {(role === 'admin' || role === 'manager') && (
      <SectionPanel
        testId="admin-panel"
        title="Admin"
        description="Pricing, plans and who can see and do what"
        icon={<ShieldCheck className="w-4 h-4" />}
        accent="bg-gray-700"
        tint="bg-gray-100 border-gray-300"
      >
        <DashboardTile
            testId="content-pricing-defaults"
            onClick={() => navigate('/staff/pricing-tiers')}
            icon={<PoundSterling className="w-4 h-4 text-emerald-700" />}
            iconWrap="bg-emerald-100"
            hoverBorder="hover:border-emerald-400"
            title="Material & Quote Pricing"
            subtitle="Trade / Approved / Elite material prices, RRP for quotes, Xero auto-follow · ProBound / ProEpoxy / ProDecor"
          />
        <DashboardTile
            testId="xero-integration"
            onClick={() => navigate('/staff/xero')}
            icon={<Zap className="w-4 h-4 text-sky-700" />}
            iconWrap="bg-sky-100"
            hoverBorder="hover:border-sky-300"
            title="Xero Integration"
            subtitle="Connect Xero · syncs products, prices and VAT every 5 minutes"
          />
        <DashboardTile
            testId="xero-product-linking"
            onClick={() => navigate('/staff/xero-linking')}
            icon={<Link2 className="w-4 h-4 text-sky-700" />}
            iconWrap="bg-sky-100"
            hoverBorder="hover:border-sky-300"
            title="Xero Product Linking"
            subtitle="Link every product to its Xero item per business · ProBound / ProEpoxy / ProTraining"
          />
        <DashboardTile
          testId="plans-tile"
          onClick={() => navigate('/staff/plans')}
          icon={<CreditCard className="w-4 h-4 text-blue-800" />}
          iconWrap="bg-blue-100"
          hoverBorder="hover:border-blue-400"
          title="ProPortal Plans"
          subtitle="Free · Web + CRM · CRM + Marketing — prices, setup fees, subscribers"
        />
        <DashboardTile
          testId="lead-wallet-tile"
          onClick={() => navigate('/staff/lead-wallet')}
          icon={<Wallet className="w-4 h-4 text-emerald-700" />}
          iconWrap="bg-emerald-100"
          hoverBorder="hover:border-emerald-400"
          title="Lead Wallet"
          subtitle="Prepaid lead credit · balances, refunds, £ per lead"
        />
        {(user?.super_admin || role === 'admin') && (
            <DashboardTile
              testId="admin-manage-ox-team"
              onClick={() => navigate('/staff/admin-team')}
              icon={<Building2 className="w-4 h-4 text-[#7A0B7A]" />}
              iconWrap="bg-[#7A0B7A]/10"
              hoverBorder="hover:border-[#7A0B7A]"
              title="Manage Admin Team"
              subtitle={`People, roles & what each person can see (${staffUsers.length})`}
            />
          )}
        <DashboardTile
            testId="open-notify-permissions"
            onClick={() => navigate('/staff/notify-permissions')}
            icon={<Bell className="w-4 h-4 text-emerald-700" />}
            iconWrap="bg-emerald-100"
            hoverBorder="hover:border-emerald-300"
            title="Notification Permissions"
            subtitle="Which roles (Admin / Manager / Staff) get each group of automated emails & texts"
          />
        <DashboardTile
            testId="email-audit"
            onClick={() => navigate('/staff/email-audit')}
            icon={<MailSearch className="w-4 h-4 text-indigo-700" />}
            iconWrap="bg-indigo-100"
            hoverBorder="hover:border-indigo-300"
            title="Email Audit"
            subtitle="Who actually received what · confirms only active staff get staff emails"
          />
      </SectionPanel>
      )}
      </SortableSections>
      {activeTab !== 'home' && (
        <div ref={contentPanelRef} className="max-w-7xl mx-auto px-3 sm:px-4 mt-5 sm:mt-6 scroll-mt-4">
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[#7A0B7A]/10 border border-[#7A0B7A]/20">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#7A0B7A]">
              {activeTab === 'installers' && (<><Users className="w-4 h-4" /> Manage Installers ({installers.length})</>)}
              {activeTab === 'staff' && (<><Building2 className="w-4 h-4" /> Manage Ox Team ({staffUsers.length})</>)}
              {activeTab === 'requests' && (<><Bell className="w-4 h-4" /> Access Requests ({pendingRequestsCount})</>)}
            </div>
            <Button
              onClick={() => navigate('/staff')}
              variant="ghost"
              size="sm"
              data-testid="close-active-panel"
              className="text-[#7A0B7A] hover:bg-[#7A0B7A]/10"
            >
              Close ✕
            </Button>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'installers' && (
          <>

            {/* Search + Broadcast */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="relative max-w-md w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search installers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/staff/sms-templates')}
                  data-testid="installers-sms-templates-btn"
                  className="border-blue-300 text-blue-800 hover:bg-blue-50"
                  title="Manage SMS text templates"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Templates
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBroadcastPickerOpen(true)}
                  data-testid="installers-broadcast-btn"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                >
                  <Megaphone className="w-4 h-4 mr-1" />
                  Broadcast
                </Button>
              </div>
            </div>
            
            {/* Installers Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-[#7A0B7A] border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading installers...</p>
              </div>
            ) : filteredInstallers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No installers found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredInstallers.map(installer => (
                  <Card key={installer.user_id} className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`installer-card-${installer.user_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-[#27292E]">{installer.name}</h3>
                          {installer.company_name && (
                            <p className="text-sm text-gray-500">{installer.company_name}</p>
                          )}
                          <div className="mt-1.5">
                            <ApprovedBadgeList certs={certsByInstaller[installer.user_id] || []} />
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${ROLE_COLORS.installer.bg} ${ROLE_COLORS.installer.text}`}>
                          Installer
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600 mb-4">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{installer.email}</span>
                        </div>
                        {installer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <span>{installer.phone}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>Joined {new Date(installer.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                        <div className="text-center flex-1">
                          <p className="text-2xl font-bold text-[#7A0B7A]">{installer.lead_count || 0}</p>
                          <p className="text-xs text-gray-500">Leads</p>
                        </div>
                        <div className="text-center flex-1">
                          <p className="text-2xl font-bold text-[#2F5E96]">{installer.quote_count || 0}</p>
                          <p className="text-xs text-gray-500">Quotes</p>
                        </div>
                      </div>
                      
                      {/* Access Badges */}
                      <div className="flex gap-1 flex-wrap mb-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          installer.access?.probound 
                            ? 'bg-[#2F5E96]/20 text-[#2F5E96]' 
                            : 'bg-gray-100 text-gray-400 line-through'
                        }`}>
                          ProBound
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          installer.access?.proepoxy 
                            ? 'bg-[#7A0B7A]/20 text-[#7A0B7A]' 
                            : 'bg-gray-100 text-gray-400 line-through'
                        }`}>
                          ProEpoxy
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          installer.access?.prodecor 
                            ? 'bg-[#D5AB09]/20 text-[#D5AB09]' 
                            : 'bg-gray-100 text-gray-400 line-through'
                        }`}>
                          ProDecor
                        </span>
                      </div>

                      {/* Pricing tier picker (staff-only) */}
                      {pricingTiers.length > 0 && (
                        <div className="mb-2 flex items-center gap-2">
                          <PoundSterling className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                          <span className="text-xs text-gray-500 shrink-0">Tier:</span>
                          <select
                            data-testid={`installer-tier-${installer.user_id}`}
                            value={installer.pricing_tier_id || ''}
                            onChange={(e) => setInstallerTier(installer.user_id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-emerald-500"
                          >
                            {!installer.pricing_tier_id && <option value="">— unassigned —</option>}
                            {pricingTiers.map((t) => (
                              <option key={t.tier_id} value={t.tier_id}>{t.label}{t.is_default ? ' (default)' : ''}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Per-brand tier picker — drives prices the installer
                          sees on Material Orders + Quote Calculators. Slugs
                          are canonical (trade / approved / elite_approved) and
                          index directly into stock_products.prices. */}
                      {['probound', 'proepoxy', 'prodecor'].some((b) => installer.access?.[b]) && (
                        <div className="mb-4 mt-1 space-y-1 border-t border-gray-100 pt-2">
                          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Tier per brand</div>
                          {['probound', 'proepoxy', 'prodecor'].map((brand) => {
                            if (!installer.access?.[brand]) return null;
                            const current = installer.pricing_tier_by_brand?.[brand] || '';
                            const accent = { probound: 'text-[#2F5E96]', proepoxy: 'text-[#7A0B7A]', prodecor: 'text-[#D4A24B]' }[brand];
                            return (
                              <div key={brand} className="flex items-center gap-2">
                                <span className={`text-[11px] font-bold capitalize shrink-0 w-16 ${accent}`}>{brand}</span>
                                <select
                                  data-testid={`installer-mct-${brand}-${installer.user_id}`}
                                  value={current}
                                  onChange={(e) => setInstallerMaterialCostTier(installer.user_id, brand, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-emerald-500"
                                >
                                  <option value="">— unassigned (Trade) —</option>
                                  {TIER_SLUGS.map((t) => (
                                    <option key={t.slug} value={t.slug}>{t.label}</option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleViewInstaller(installer)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a]"
                            onClick={() => handleAddLeadToInstaller(installer)}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Lead
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                            disabled={!installer.phone || installer.sms_opt_in === false}
                            onClick={() => setMessageTarget({ name: installer.name, phone: installer.phone, ref: installer.user_id, channel: 'sms' })}
                            data-testid={`installer-sms-${installer.user_id}`}
                            title={
                              !installer.phone
                                ? 'No phone number on this installer'
                                : installer.sms_opt_in === false
                                  ? 'Installer opted out of SMS'
                                  : 'Send an SMS via Firetext'
                            }
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            SMS Text
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                            disabled={!installer.phone || installer.whatsapp_opt_in === false}
                            onClick={() => setMessageTarget({ name: installer.name, phone: installer.phone, ref: installer.user_id, channel: 'whatsapp' })}
                            data-testid={`installer-whatsapp-${installer.user_id}`}
                            title={
                              !installer.phone
                                ? 'No phone number on this installer'
                                : installer.whatsapp_opt_in === false
                                  ? 'Installer opted out of WhatsApp'
                                  : 'Send a WhatsApp via Superchat'
                            }
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            WhatsApp
                          </Button>
                        </div>

                        {/* Consent toggles — small, unobtrusive. Existing installers
                            default to opted-in until a staff member flips them off. */}
                        <div className="flex items-center gap-3 text-[10px] text-gray-500 pt-1">
                          <label className="flex items-center gap-1 cursor-pointer" data-testid={`installer-sms-optin-label-${installer.user_id}`}>
                            <input
                              type="checkbox"
                              checked={installer.sms_opt_in !== false}
                              onChange={(e) => toggleInstallerConsent(installer.user_id, 'sms_opt_in', e.target.checked)}
                              className="w-3 h-3 accent-blue-600"
                              data-testid={`installer-sms-optin-${installer.user_id}`}
                            />
                            SMS OK
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer" data-testid={`installer-wa-optin-label-${installer.user_id}`}>
                            <input
                              type="checkbox"
                              checked={installer.whatsapp_opt_in !== false}
                              onChange={(e) => toggleInstallerConsent(installer.user_id, 'whatsapp_opt_in', e.target.checked)}
                              className="w-3 h-3 accent-emerald-600"
                              data-testid={`installer-wa-optin-${installer.user_id}`}
                            />
                            WhatsApp OK
                          </label>
                        </div>
                      </div>
                      <InstallerRecentMessages
                        installerId={installer.user_id}
                        refreshKey={historyRefreshKey}
                        onViewThread={() => openThread(installer)}
                        unreadCount={unreadCounts[installer.user_id] || 0}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Compose modal for SMS / WhatsApp — reused for every installer card AND for broadcasts. */}
            <SendMessageModal
              open={Boolean(messageTarget)}
              onClose={() => setMessageTarget(null)}
              channel={messageTarget?.channel || 'sms'}
              recipientName={messageTarget?.name}
              recipientPhone={messageTarget?.phone}
              recipientRef={messageTarget?.ref}
              onSent={() => setHistoryRefreshKey((v) => v + 1)}
            />

            {/* Broadcast picker (choose brands + channel) then reuse the same
                compose modal in broadcast mode. */}
            <BroadcastPicker
              open={broadcastPickerOpen}
              onClose={() => setBroadcastPickerOpen(false)}
              installers={installers}
              onPick={(cfg) => {
                setBroadcastPickerOpen(false);
                setBroadcastConfig(cfg);
              }}
            />
            <SendMessageModal
              open={Boolean(broadcastConfig)}
              onClose={() => setBroadcastConfig(null)}
              channel={broadcastConfig?.channel || 'sms'}
              broadcast={broadcastConfig ? {
                label: broadcastConfig.label,
                brand_access: broadcastConfig.brand_access,
              } : null}
              onSent={() => setHistoryRefreshKey((v) => v + 1)}
            />

            {/* Full conversation view for a single installer. */}
            <MessageThreadModal
              open={Boolean(threadTarget)}
              onClose={() => setThreadTarget(null)}
              installerId={threadTarget?.id}
              installerName={threadTarget?.name}
            />
          </>
        )}
        
        {activeTab === 'staff' && canCreateAccounts && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-[#27292E]">Admin Team</h2>
                <p className="text-xs text-gray-500 mt-0.5">{staffUsers.length} staff member{staffUsers.length === 1 ? '' : 's'} · admins, managers and team</p>
              </div>
              <Button
                onClick={() => handleOpenCreateModal('staff')}
                size="sm"
                className="bg-[#7A0B7A] text-white hover:bg-[#5e095e]"
                data-testid="add-staff-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-1.5" />
                Add staff member
              </Button>
            </div>

            {staffUsers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No staff accounts found</p>
                <Button
                  onClick={() => handleOpenCreateModal('staff')}
                  size="sm"
                  variant="outline"
                  className="mt-4"
                  data-testid="add-staff-member-empty-btn"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Add the first staff member
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {staffUsers.map(staff => {
                  const isSelf = staff.user_id === user?.user_id;
                  const canDelete = canDeleteStaff(staff);
                  return (
                    <Card key={staff.user_id} className="hover:shadow-lg transition-shadow" data-testid={`staff-card-${staff.user_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3 gap-2">
                          <div className="min-w-0">
                            <h3 className="font-bold text-[#27292E] truncate">{staff.name}{isSelf && <span className="ml-1.5 text-[10px] font-semibold text-gray-400">(you)</span>}</h3>
                            <p className="text-sm text-gray-500 truncate">{staff.email}</p>
                            <StaffMobileField staff={staff} onSaved={fetchStaffUsers} />
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium shrink-0 ${ROLE_COLORS[staff.role]?.bg} ${ROLE_COLORS[staff.role]?.text}`}>
                            {staff.role?.charAt(0).toUpperCase() + staff.role?.slice(1)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs text-gray-500">
                            <Calendar className="w-3.5 h-3.5 inline mr-1" />
                            Joined {new Date(staff.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                              onClick={() => handleResendInvite(staff)}
                              size="sm"
                              variant="outline"
                              className="border-purple-200 text-purple-700 hover:bg-purple-50"
                              data-testid={`staff-resend-invite-${staff.user_id}`}
                              title={`Resend "Set password" email to ${staff.email}`}
                              aria-label={`Resend invite to ${staff.name}`}
                            >
                              <Mail className="w-3.5 h-3.5 mr-1" />
                              Resend invite
                            </Button>
                            <Button
                              onClick={() => handleDeleteStaff(staff)}
                              disabled={!canDelete}
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`staff-delete-${staff.user_id}`}
                              title={
                                isSelf ? "You can't remove your own account"
                                : (role === 'admin' && staff.role === 'admin') ? "Admin accounts can't be deleted"
                                : (role === 'manager' && staff.created_by !== user?.user_id) ? "Managers can only remove accounts they created"
                                : `Remove ${staff.name}`
                              }
                              aria-label={`Remove ${staff.name}`}
                            >
                              <X className="w-3.5 h-3.5 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </div>

                        {role === 'admin' && staff.role !== 'admin' && (
                          <label className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={!!staff.can_view_profit} onChange={(e) => handleProfitAccess(staff, e.target.checked)} data-testid={`staff-profit-access-${staff.user_id}`} />
                            <span><span className="font-semibold">Can see profit &amp; costs</span> <span className="text-gray-400">— unlocks the Profit Centre</span></span>
                          </label>
                        )}
                        {role === 'admin' && staff.role !== 'admin' && (
                          <label className="mt-2 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={!!staff.can_manage_invoices} onChange={(e) => handleInvoiceAccess(staff, e.target.checked)} data-testid={`staff-invoice-access-${staff.user_id}`} />
                            <span><span className="font-semibold">Can manage invoices</span> <span className="text-gray-400">— unlocks the Invoice Centre (all three businesses)</span></span>
                          </label>
                        )}
                        {/* Reports-to picker (team only) — drives stock approval routing. */}
                        {staff.role === 'team' && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Reports to</label>
                            <select
                              value={staff.reports_to_user_id || ''}
                              onChange={(e) => handleUpdateReportsTo(staff, e.target.value)}
                              className="w-full p-2 text-sm border rounded-lg bg-white"
                              data-testid={`staff-reports-to-${staff.user_id}`}
                            >
                              <option value="">— Not set (no approver) —</option>
                              {staffUsers
                                .filter(s => (s.role === 'admin' || s.role === 'manager') && s.user_id !== staff.user_id)
                                .map(s => (
                                  <option key={s.user_id} value={s.user_id}>
                                    {s.name} ({s.role})
                                  </option>
                                ))}
                            </select>
                            <p className="text-[10px] text-gray-400 mt-1">Approves this person&apos;s stock changes. Any manager/admin can also approve.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Access Requests Tab */}
        {activeTab === 'requests' && (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <h2 className="text-xl font-bold text-[#27292E] mr-2">Access Requests</h2>
              {[['pending', 'Pending'], ['approved', 'Approved'], ['denied', 'Denied'], ['all', 'All']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setRequestFilter(k)} data-testid={`access-filter-${k}`}
                  className={`text-xs font-semibold px-3 py-1 rounded-full border ${requestFilter === k ? 'bg-[#27292E] text-white border-[#27292E]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  {l} <span className="opacity-70">({k === 'all' ? accessRequests.length : (requestCounts[k] || 0)})</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Staff get <strong>one daily summary email</strong> (09:00) of pending requests with a link straight here — once anyone approves or denies a request it leaves Pending. Check Approved / Denied to see who dealt with it.
              {role === 'admin' && (
                <button type="button" className="ml-2 underline text-[#7A0B7A] font-semibold" data-testid="access-digest-send-now"
                  onClick={async () => {
                    try {
                      const r = await fetch(`${API_URL}/api/access-requests/digest/run`, { method: 'POST', credentials: 'include' });
                      const d = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(d.detail || 'Failed');
                      toast.success(d.pending ? `Summary sent to ${d.sent} staff (${d.pending} pending)` : 'Nothing pending — no email sent');
                    } catch (e) { toast.error(e.message); }
                  }}>Send summary now</button>
              )}
            </p>

            {visibleRequests.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200" data-testid="access-requests-empty">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No {requestFilter === 'all' ? '' : requestFilter} access requests</p>
                <p className="text-sm text-gray-400 mt-1">When installers request access to sections, they&apos;ll appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {visibleRequests.map(request => (
                  <Card key={request.id} id={`access-request-${request.id}`} data-testid={`access-request-card-${request.id}`} className={`hover:shadow-lg transition-shadow ${request.id === highlightRequestId ? 'ring-2 ring-[#7A0B7A] ring-offset-2' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-bold text-[#27292E]">{request.user_name}</h3>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold text-white ${
                              request.section === 'probound' ? 'bg-[#2F5E96]' :
                              request.section === 'proepoxy' ? 'bg-[#7A0B7A]' :
                              request.section === 'prodecor' ? 'bg-[#D5AB09]' : 'bg-gray-500'
                            }`}>
                              {request.section.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{request.user_email}</p>
                          {request.message && (
                            <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">{request.message}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            Requested {new Date(request.created_at).toLocaleDateString('en-GB', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                            {request.status !== 'pending' && (
                              <span className={`ml-2 font-semibold ${request.status === 'approved' ? 'text-emerald-700' : 'text-rose-700'}`} data-testid={`access-request-outcome-${request.id}`}>
                                · {request.status === 'approved' ? 'Approved' : 'Denied'} by {request.reviewed_by_name || 'staff'}{request.reviewed_at ? ` on ${new Date(request.reviewed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                              </span>
                            )}
                          </p>
                        </div>
                        {request.status === 'pending' && <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveRequest(request.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleDenyRequest(request.id)}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Deny
                          </Button>
                        </div>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Installer Details Modal */}
      {selectedInstaller && installerDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{selectedInstaller.name}</h2>
                <p className="text-gray-500">{selectedInstaller.company_name || selectedInstaller.email}</p>
              </div>
              <button
                onClick={() => { setSelectedInstaller(null); setInstallerDetails(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-[#7A0B7A]/10 rounded-xl text-center">
                  <p className="text-3xl font-bold text-[#7A0B7A]">{installerDetails.lead_count}</p>
                  <p className="text-sm text-gray-600">Total Leads</p>
                </div>
                <div className="p-4 bg-[#2F5E96]/10 rounded-xl text-center">
                  <p className="text-3xl font-bold text-[#2F5E96]">{installerDetails.quote_count}</p>
                  <p className="text-sm text-gray-600">Total Quotes</p>
                </div>
              </div>
              
              {/* Section Access Control */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                <h3 className="font-bold mb-3">Section Access</h3>
                <p className="text-sm text-gray-500 mb-4">Enable/disable which sections this installer can access</p>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={installerDetails.installer?.access?.probound || false}
                      onChange={(e) => handleUpdateAccess(selectedInstaller.user_id, {
                        ...installerDetails.installer?.access,
                        probound: e.target.checked
                      })}
                      className="w-5 h-5 rounded border-gray-300 text-[#2F5E96] focus:ring-[#2F5E96]"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-[#2F5E96]">ProBound</span>
                      <p className="text-xs text-gray-500">Resin Bound Surfacing</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={installerDetails.installer?.access?.proepoxy || false}
                      onChange={(e) => handleUpdateAccess(selectedInstaller.user_id, {
                        ...installerDetails.installer?.access,
                        proepoxy: e.target.checked
                      })}
                      className="w-5 h-5 rounded border-gray-300 text-[#7A0B7A] focus:ring-[#7A0B7A]"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-[#7A0B7A]">ProEpoxy</span>
                      <p className="text-xs text-gray-500">Epoxy Floor Systems</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={installerDetails.installer?.access?.prodecor || false}
                      onChange={(e) => handleUpdateAccess(selectedInstaller.user_id, {
                        ...installerDetails.installer?.access,
                        prodecor: e.target.checked
                      })}
                      className="w-5 h-5 rounded border-gray-300 text-[#D5AB09] focus:ring-[#D5AB09]"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-[#D5AB09]">ProDecor</span>
                      <p className="text-xs text-gray-500">Decorative Walling</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Recent Leads */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold">Recent Leads</h3>
                  <Button
                    size="sm"
                    className="bg-[#7A0B7A] hover:bg-[#5a085a]"
                    onClick={() => handleAddLeadToInstaller(selectedInstaller)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Lead
                  </Button>
                </div>
                {installerDetails.leads?.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">No leads yet</p>
                ) : (
                  <div className="space-y-2">
                    {installerDetails.leads?.slice(0, 5).map(lead => (
                      <div key={lead.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium">{lead.customer_name}</p>
                          <div className="flex gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded text-xs ${SERVICE_COLORS[lead.service_type]?.bg} ${SERVICE_COLORS[lead.service_type]?.text}`}>
                              {lead.service_type}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[lead.status]?.bg} ${STATUS_COLORS[lead.status]?.text}`}>
                              {lead.status}
                            </span>
                            {lead.added_by && (
                              <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                                Added by {lead.added_by_name || 'Staff'}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Recent Quotes */}
              <div>
                <h3 className="font-bold mb-3">Recent Quotes</h3>
                {installerDetails.quotes?.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">No quotes yet</p>
                ) : (
                  <div className="space-y-2">
                    {installerDetails.quotes?.slice(0, 5).map(quote => (
                      <div key={quote.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium">{quote.name || quote.customer_name || 'Untitled Quote'}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(quote.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="font-bold text-[#7A0B7A]">
                          £{quote.total_price?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Account Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">{createScope === 'staff' ? 'Add Staff Member' : 'Create Account'}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateAccount} className="p-6 space-y-4">
              <div>
                <Label>Account Type</Label>
                <select
                  value={newAccount.role}
                  onChange={(e) => setNewAccount({ ...newAccount, role: e.target.value })}
                  className="w-full mt-1 p-2.5 border-2 rounded-lg"
                  data-testid="create-account-role"
                >
                  {getCreatableRoles()
                    .filter(r => createScope === 'staff' ? r !== 'installer' : true)
                    .map(r => (
                      <option key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </option>
                    ))}
                </select>
              </div>
              
              <div>
                <Label>Name *</Label>
                <Input
                  value={newAccount.name}
                  onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newAccount.email}
                  onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                  required
                />
              </div>
              
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex gap-3" data-testid="invite-email-notice">
                <Mail className="w-4 h-4 text-purple-700 mt-0.5 shrink-0" />
                <div className="text-xs text-purple-900 leading-relaxed">
                  An <strong>invite email</strong> will be sent to this address. They&apos;ll click a one-time link and pick their own password. The link is valid for 7 days.
                </div>
              </div>

              {newAccount.role === 'team' && (
                <div>
                  <Label>Reports to *</Label>
                  <select
                    value={newAccount.reports_to_user_id || ''}
                    onChange={(e) => setNewAccount({ ...newAccount, reports_to_user_id: e.target.value })}
                    className="w-full mt-1 p-2.5 border-2 rounded-lg"
                    data-testid="create-account-reports-to"
                    required
                  >
                    <option value="">Select an approving manager…</option>
                    {staffUsers
                      .filter(s => s.role === 'admin' || s.role === 'manager')
                      .map(s => (
                        <option key={s.user_id} value={s.user_id}>
                          {s.name} ({s.role})
                        </option>
                      ))}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">This person approves all stock changes the team member makes. Any other manager can also approve if they&apos;re off.</p>
                </div>
              )}
              
              {newAccount.role === 'installer' && (
                <>
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={newAccount.company_name}
                      onChange={(e) => setNewAccount({ ...newAccount, company_name: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={newAccount.phone}
                      onChange={(e) => setNewAccount({ ...newAccount, phone: e.target.value })}
                    />
                  </div>
                  
                  {/* Section Access */}
                  <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200" data-testid="create-account-access">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="block">Areas open from their first login</Label>
                      <button type="button" className="text-[11px] font-semibold text-emerald-800 underline" data-testid="create-account-access-all"
                        onClick={() => setNewAccount({ ...newAccount, access: { probound: true, proepoxy: true, prodecor: true, protraining: true } })}>Select all</button>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">Ticked areas are unlocked the moment they log in — no access request needed. Anything left unticked they'll have to request.</p>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAccount.access?.probound || false}
                          onChange={(e) => setNewAccount({
                            ...newAccount,
                            access: { ...newAccount.access, probound: e.target.checked }
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-[#2F5E96] focus:ring-[#2F5E96]"
                          data-testid="create-account-access-probound"
                        />
                        <span className="text-sm font-medium text-[#2F5E96]">ProBound</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAccount.access?.proepoxy || false}
                          onChange={(e) => setNewAccount({
                            ...newAccount,
                            access: { ...newAccount.access, proepoxy: e.target.checked }
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-[#7A0B7A] focus:ring-[#7A0B7A]"
                          data-testid="create-account-access-proepoxy"
                        />
                        <span className="text-sm font-medium text-[#7A0B7A]">ProEpoxy</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAccount.access?.prodecor || false}
                          onChange={(e) => setNewAccount({
                            ...newAccount,
                            access: { ...newAccount.access, prodecor: e.target.checked }
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-[#D5AB09] focus:ring-[#D5AB09]"
                          data-testid="create-account-access-prodecor"
                        />
                        <span className="text-sm font-medium text-[#D5AB09]">ProDecor</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAccount.access?.protraining || false}
                          onChange={(e) => setNewAccount({
                            ...newAccount,
                            access: { ...newAccount.access, protraining: e.target.checked }
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-[#1f4030] focus:ring-[#1f4030]"
                          data-testid="create-account-access-protraining"
                        />
                        <span className="text-sm font-medium text-[#1f4030]">ProTraining <span className="text-gray-400 font-normal">(course notes, videos, documents)</span></span>
                      </label>
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a]"
                  data-testid="create-account-submit"
                >
                  Send invite
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </TileFavouritesProvider>
  );
};

export default StaffDashboard;

// ---------- Section helpers (presentational) ----------

const CustomiseDashboardButton = () => {
  const favCtx = useTileFavourites();
  if (!favCtx) return null;
  const on = !!favCtx.customising;
  return (
    <Button size="sm" variant={on ? 'default' : 'outline'} onClick={() => favCtx.setCustomising(!on)} data-testid="customise-dashboard-btn" aria-pressed={on}
      className={`px-2 sm:px-3 shrink-0 ${on ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'border-gray-300 text-gray-700'}`} title="Rearrange, hide or collapse tiles and sections">
      <LayoutGrid className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{on ? 'Done customising' : 'Customise dashboard'}</span>
    </Button>
  );
};

const SectionPanel = ({ title, description, icon, accent, tint, testId, children }) => {
  const favCtx = useTileFavourites();
  const collapsed = !!favCtx?.collapsed?.includes(testId);
  const header = (
    <>
      <SectionHandle id={testId} />
      <button type="button" onClick={() => favCtx?.toggleCollapsed(testId)} aria-expanded={!collapsed} className="flex-1 min-w-0 flex items-center gap-2.5 sm:gap-3 text-left" title={collapsed ? 'Expand section' : 'Collapse section'} data-testid={`${testId}-title`}>
        <span className={`inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl shrink-0 shadow-sm ${tint ? `${accent} text-white` : accent}`}>{icon}</span>
        <span className="min-w-0">
          <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight leading-tight truncate">{title}</h2>
          {description && !collapsed && <p className="text-[11px] sm:text-xs text-gray-500 leading-snug truncate" data-testid={`${testId}-description`}>{description}</p>}
        </span>
      </button>
    </>
  );
  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-4 mt-5 sm:mt-6" data-testid={testId} data-collapsed={collapsed || undefined}>
      <SortableSection sectionId={testId} header={header} headerClass={`${tint || 'bg-gray-50 border-gray-200'}`}>{children}</SortableSection>
    </section>
  );
};

const DashboardTile = ({ testId, onClick, icon, iconWrap, hoverBorder, title, subtitle, badge, badgeTone, favouriteOf }) => {
  const favCtx = useTileFavourites();
  const { can } = useContext(AuthContext);
  const favId = favouriteOf || testId;
  const feature = TILE_FEATURES[favId];
  const allowed = !feature || can(feature);
  if (favCtx && !favouriteOf && allowed) favCtx.register(testId, { onClick, icon, iconWrap, hoverBorder, title, subtitle, badge, badgeTone });
  if (!allowed) return null;
  const badgeBg = badgeTone === 'emerald'
    ? 'bg-emerald-600'
    : badgeTone === 'rose'
      ? 'bg-rose-600'
      : badgeTone === 'amber'
        ? 'bg-amber-500'
        : 'bg-red-600';
  return (
    <div className="relative group">
    <button
      data-testid={testId}
      onClick={onClick}
      className={`relative w-full text-left flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 bg-white ${hoverBorder} hover:shadow-sm transition-all`}
    >
      {badge > 0 && (
        <span
          className={`absolute -top-1.5 -left-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full ${badgeBg} text-white text-[10px] font-bold ring-2 ring-white shadow-md`}
          data-testid={`${testId}-badge`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <div className="flex items-center gap-3 min-w-0">
        <div className={`p-2 ${iconWrap} rounded-lg shrink-0`}>{icon}</div>
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 text-sm">{title}</div>
          <div className="text-xs text-gray-500 truncate">{subtitle}</div>
        </div>
      </div>
      <span className="text-gray-400 text-sm shrink-0">→</span>
    </button>
    {favCtx && <FavouriteStar id={favId} />}
    </div>
  );
};


const UK_MOBILE = /^(\+44|0)7\d{9}$/;
function StaffMobileField({ staff, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(staff.phone || '');
  const [busy, setBusy] = useState(false);
  const clean = (staff.phone || '').replace(/[\s-]/g, '');
  const ok = UK_MOBILE.test(clean);
  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/users/${staff.user_id}/mobile`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: val }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Could not save');
      toast.success(d.phone ? `Mobile saved for ${staff.name}` : 'Mobile cleared');
      setEditing(false); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="mt-1 flex items-center gap-1" data-testid={`staff-mobile-edit-${staff.user_id}`}>
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} placeholder="07xxx xxxxxx" autoFocus
          className="h-7 w-36 text-xs border rounded px-2" data-testid={`staff-mobile-input-${staff.user_id}`} />
        <button type="button" onClick={save} disabled={busy} className="text-xs font-bold text-emerald-700 px-1.5" data-testid={`staff-mobile-save-${staff.user_id}`}>{busy ? '…' : 'Save'}</button>
        <button type="button" onClick={() => setEditing(false)} className="text-xs text-gray-500 px-1">Cancel</button>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => { setVal(staff.phone || ''); setEditing(true); }} data-testid={`staff-mobile-${staff.user_id}`}
      className={`mt-1 inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border ${ok ? 'text-gray-600 border-gray-200 hover:border-gray-400' : 'text-amber-800 bg-amber-50 border-amber-300 font-semibold hover:bg-amber-100'}`}
      title={ok ? 'Change mobile' : 'No mobile on file — texts like the Friday hours nudge can\'t reach this person. Click to add.'}>
      {ok ? <><Phone className="w-3 h-3" /> {staff.phone}</> : <><AlertTriangle className="w-3 h-3" /> No mobile — add one</>}
    </button>
  );
}
